import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { THEMES } from '../themes/presets.js';

// Storage-prefix sweep target (spec §14). No migration of the old key's
// VALUE is attempted (spec §2.5) — it's JSON-shaped ({themeId, customColors})
// under the old 4-preset system and has nothing meaningful to carry over into
// a 3-way Auto/Paper/Ink choice. We only peek at its presence once, below.
export const STORAGE_KEY = 'etch.theme';
const LEGACY_STORAGE_KEY = 'onboarding-tutor-theme';

const VALID_CHOICES = new Set(['auto', 'paper', 'ink']);

/**
 * Pure resolution of a user choice + OS preference into a concrete theme.
 * No DOM access — exported so tests/unit/web/theme.test.js can cover it
 * without jsdom (see house rules: jsdom is not configured for this repo).
 */
export function resolveTheme(choice, prefersDark) {
  if (choice === 'paper' || choice === 'ink') return choice;
  return prefersDark ? 'ink' : 'paper';
}

function readStoredChoice() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (VALID_CHOICES.has(stored)) return stored;
    // Any unrecognised value (including nothing stored) falls back to 'auto'.
    // A present-but-foreign legacy key doesn't change that fallback — its
    // shape is ignored entirely, it's read only so this doesn't throw.
    if (stored == null) localStorage.getItem(LEGACY_STORAGE_KEY);
  } catch {
    // Private-mode/SSR — fall back silently.
  }
  return 'auto';
}

function getPrefersDark() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyResolvedTheme(choice, resolved) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.themeChoice = choice;
}

// Runs on import, before React mounts, so the very first paint already has
// the right data-theme attribute (avoids a flash of the wrong theme). This is
// the one deliberate module-level side effect in this file.
if (typeof document !== 'undefined') {
  const initialChoice = readStoredChoice();
  applyResolvedTheme(initialChoice, resolveTheme(initialChoice, getPrefersDark()));
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [choice, setChoiceState] = useState(readStoredChoice);
  const [prefersDark, setPrefersDark] = useState(getPrefersDark);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => setPrefersDark(event.matches);
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  const resolved = resolveTheme(choice, prefersDark);

  // Inline styles on :root would shadow themes.css's :root[data-theme] block
  // (spec §17.5 / house rules) — this provider only ever toggles the
  // attribute, never sets a CSS custom property directly.
  useEffect(() => {
    applyResolvedTheme(choice, resolved);
  }, [choice, resolved]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      // Private mode — theme just won't persist across reloads.
    }
  }, [choice]);

  const setChoice = useCallback((next) => {
    setChoiceState(VALID_CHOICES.has(next) ? next : 'auto');
  }, []);

  const value = useMemo(() => ({ choice, resolved, setChoice, themes: THEMES }), [choice, resolved, setChoice]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
